import { useEffect, useCallback, useRef } from "react";

/**
 * PERFORMANCE-OPTIMIZED INFINITE SCROLL
 * Uses Intersection Observer API
 */
export const useInfiniteScroll = ({
  loading,
  hasMore,
  onLoadMore,
  threshold = 0.1, // Load when 10% visible (lower = triggers earlier)
  rootMargin = "200px", // Load 200px before reaching the element
}) => {
  const observerRef = useRef(null);
  const loadMoreRef = useRef(onLoadMore);

  // Update ref when callback changes
  useEffect(() => {
    loadMoreRef.current = onLoadMore;
  }, [onLoadMore]);

  // Create intersection observer
  const lastElementRef = useCallback(
    (node) => {
      if (loading) return;

      // Disconnect previous observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      // Create new observer
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];

          if (entry.isIntersecting && hasMore && !loading) {
            loadMoreRef.current();
          }
        },
        {
          threshold,
          rootMargin, // Trigger earlier before element is visible
        }
      );

      // Observe new node
      if (node) {
        observerRef.current.observe(node);
      }
    },
    [loading, hasMore, threshold, rootMargin]
  );

  return { lastElementRef };
};
